/**
 * @fileoverview Video component for rendering localized clips with ClipStyle
 * @module screen-view-v2/components/shared/Video
 *
 * V2 adaptation:
 * - LocalizedClip replaces VideoType
 * - resolveClipReference() instead of resolveAssetUrl()
 * - mapClipStyles() instead of inline mapVideoBlockStyles()
 * - mapCornerRadiusToCSS() instead of manual cornerRadius number
 * - No box/mapBoxBlockStyles — LocalizedClip has no box property
 * - Size 'original' instead of 'origin'
 *
 * Preserves isImageUrl fallback pattern (renders <img> if URL is an image).
 */

import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { LocalizedClip } from '@segmently/schema-models/v2';
import { resolveClipReference, type AssetSize } from '../../../utils/contentResolver';
import { mapClipStyles, mapCornerRadiusToCSS } from '../../../utils/styleMapper';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { DEFAULT_PREVIEW_MODE, type PreviewMode } from '../../../types/previewMode';
import styles from './Video.module.css';

/**
 * V2 scale mode map — only V2 values
 */
const SCALE_MODE_MAP: Record<string, CSSProperties['objectFit']> = {
  scaleToFill: 'fill',
  scaleAspectFit: 'contain',
  scaleAspectFill: 'cover',
  center: 'none',
  top: 'none',
  bottom: 'none',
  left: 'none',
  right: 'none',
};

/** Article template (G2, D6): native article video; absent = today's funnel video (autoplay, muted, no controls). */
export interface ArticleVideoOptions {
  controls: boolean;
  autoplay: boolean;
  preload: 'none' | 'metadata' | 'auto';
  /** Absolute URL; overrides the small-clip poster. */
  poster?: string;
}

export interface VideoProps {
  /** V2: LocalizedClip replaces VideoType */
  clip: LocalizedClip | undefined;
  locale: string;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  fillContainer?: boolean;
  scaleMode?: string;
  repeat?: boolean;
  previewMode?: PreviewMode;
  cornerRadius?: number;
  articleOptions?: ArticleVideoOptions;
}

export const Video = memo(function Video({
  clip,
  locale,
  className,
  'data-testid': testId,
  editPath,
  fillContainer = false,
  scaleMode: scaleModeOverride,
  repeat: repeatOverride,
  previewMode,
  cornerRadius,
  articleOptions,
}: VideoProps): React.ReactElement | null {
  if (!clip) {
    return null;
  }

  // V2: 'original' instead of 'origin'
  const mode = previewMode ?? DEFAULT_PREVIEW_MODE;
  const assetSize: AssetSize = mode === 'static' ? 'small' : 'original';
  const src = resolveClipReference(clip, locale, assetSize);
  const shouldAutoPlay = articleOptions ? articleOptions.autoplay : mode !== 'static';

  // Poster thumbnail: 'small' variant is an image preview of the video.
  // Shows immediately while video buffers — prevents black frame flash.
  const posterSrc = mode !== 'static' ? resolveClipReference(clip, locale, 'small') : null;
  const posterUrl = articleOptions?.poster ?? ((posterSrc && posterSrc !== src) ? posterSrc : undefined);

  if (src === null) {
    return null;
  }

  // Detect if URL is actually an image (handles incorrect MediaKind in data)
  const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'];
  const urlLower = src.toLowerCase();
  const isImageUrl = imageExtensions.some(ext => urlLower.includes(ext));

  // V2: mapClipStyles replaces inline mapVideoBlockStyles
  const clipStyles = mapClipStyles(clip.appearance);

  // V2: cornerRadii via mapCornerRadiusToCSS instead of manual number
  const cornerRadiusStyles = mapCornerRadiusToCSS(clip.appearance?.cornerRadii);

  // V2: appearance.scaleMode instead of styles.scaleMode
  const scaleMode = scaleModeOverride ?? clip.appearance?.scaleMode;
  const objectFit = scaleMode ? SCALE_MODE_MAP[scaleMode] : undefined;

  const scaleModeClass = scaleMode ? styles[scaleMode as keyof typeof styles] : '';

  // V2: appearance.repeat instead of styles.repeat
  const shouldLoop = repeatOverride ?? clip.appearance?.repeat === true;

  const videoStyle: CSSProperties = {
    display: 'block',
    ...clipStyles,
    ...cornerRadiusStyles,
    ...(objectFit && { objectFit }),
    ...(fillContainer && {
      width: '100%',
      height: '100%',
      objectFit: objectFit ?? ('cover' as const),
    }),
    ...(cornerRadius !== undefined && { borderRadius: cornerRadius }),
  };

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  return (
    <div
      className={styles.videoWrapper}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      {isImageUrl ? (
        <img
          src={src}
          alt=""
          className={`${styles.video}${scaleModeClass ? ` ${scaleModeClass}` : ''}${className ? ` ${className}` : ''}`}
          style={videoStyle}
          data-testid={schemaTestId ? `${schemaTestId}.img` : undefined}
        />
      ) : (
        <video
          src={src}
          poster={posterUrl}
          className={`${styles.video}${scaleModeClass ? ` ${scaleModeClass}` : ''}${className ? ` ${className}` : ''}`}
          style={videoStyle}
          data-testid={schemaTestId ? `${schemaTestId}.video` : undefined}
          autoPlay={shouldAutoPlay}
          muted={articleOptions ? articleOptions.autoplay : true}
          loop={shouldLoop}
          playsInline
          controls={articleOptions?.controls}
          preload={articleOptions?.preload}
        />
      )}
    </div>
  );
});

Video.displayName = 'Video';
